// src/composables/useI18n.ts
import { ref, computed, shallowReactive } from 'vue'
import { localeLoaders, supportedLocales, type LocaleKey } from '@/locales'
import apiClient from '@/services/api'
import { sharedCookieDomain } from '@/utils/siteHosts'

const VALID_LOCALES: LocaleKey[] = ['ru', 'ky', 'tr']

// localStorage is per-origin, so a language picked on itorgo.kg never reached
// admin.itorgo.kg (and vice versa). A cookie on the parent domain is shared by
// both hosts, so it is read first and written on every change.
function readLocaleCookie(): LocaleKey | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)itorgo_locale=([a-z]{2})/)
  const value = match?.[1] as LocaleKey | undefined
  return value && VALID_LOCALES.includes(value) ? value : null
}

function writeLocaleCookie(locale: LocaleKey): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const host = window.location.hostname
  const domain = sharedCookieDomain(host)
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `itorgo_locale=${locale}; path=/; max-age=31536000; SameSite=Lax${domain}${secure}`
}

function readLocaleParam(): LocaleKey | null {
  if (typeof window === 'undefined') return null
  const value = new URLSearchParams(window.location.search).get('lang') as LocaleKey | null
  return value && VALID_LOCALES.includes(value) ? value : null
}

function getStoredLocale(): LocaleKey | null {
  const fromParam = readLocaleParam()
  if (fromParam) return fromParam
  const fromCookie = readLocaleCookie()
  if (fromCookie) return fromCookie
  if (typeof localStorage === 'undefined') return null
  const itorgoLocale = localStorage.getItem('itorgo_locale') as LocaleKey | null
  if (itorgoLocale && ['ru', 'ky', 'tr'].includes(itorgoLocale)) return itorgoLocale
  const legacyLocale = localStorage.getItem('locale') as LocaleKey | null
  if (legacyLocale && ['ru', 'ky', 'tr'].includes(legacyLocale)) return legacyLocale
  return null
}

/* ------------------------------------------------------------------ *
 * Pluralisation
 *
 * Russian agrees the noun (and its adjective) with the numeral, so a
 * single hard-coded form produced "Найдено 1 лотов" / "1 категорий".
 * Kyrgyz and Turkish keep the noun in the singular after a numeral, so
 * one written form covers every count there.
 * ------------------------------------------------------------------ */

type PluralForm = 'one' | 'few' | 'many' | 'other'

/** Positional order used by both `{ one, few, many }` bundles and the word table. */
const PLURAL_ORDER: PluralForm[] = ['one', 'few', 'many']

/** Parameter names that carry a countable quantity. */
const COUNT_PARAMS = ['n', 'count', 'total']

function pluralForm(locale: LocaleKey, value: number): PluralForm {
  if (locale !== 'ru') return 'other'
  const abs = Math.abs(value)
  // Fractions take the genitive singular in Russian ("1,5 лота"), i.e. the "few" slot.
  if (!Number.isInteger(abs)) return 'few'
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return 'one'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few'
  return 'many'
}

// The locale files ship ONE written form per count string ("Найдено {n} лотов",
// "{n} Активных лотов", "{n} категорий"). Locale files are owned elsewhere, so
// the forms are reconstructed here: each row is [one, few, many] and every one
// of its spellings is registered as a lookup key, letting t() re-inflect the
// word(s) that directly follow a substituted count.

// Adjectives may precede the counted noun: "{n} активных лотов".
const RU_ADJECTIVE_FORMS: [string, string, string][] = [
  ['активный', 'активных', 'активных'],
  ['успешная', 'успешных', 'успешных'],
]

// Only the FIRST noun after the numeral is inflected, so a genitive tail such as
// "{count} запросов продавцов" can never be mangled into "1 запрос продавец".
const RU_NOUN_FORMS: [string, string, string][] = [
  ['лот', 'лота', 'лотов'],
  ['категория', 'категории', 'категорий'],
  ['ставка', 'ставки', 'ставок'],
  ['день', 'дня', 'дней'],
  ['минута', 'минуты', 'минут'],
  ['секунда', 'секунды', 'секунд'],
  ['просмотр', 'просмотра', 'просмотров'],
  ['отзыв', 'отзыва', 'отзывов'],
  ['сделка', 'сделки', 'сделок'],
  ['продажа', 'продажи', 'продаж'],
  ['продавец', 'продавца', 'продавцов'],
  ['заявка', 'заявки', 'заявок'],
  ['пользователь', 'пользователя', 'пользователей'],
  ['результат', 'результата', 'результатов'],
  ['файл', 'файла', 'файлов'],
  ['час', 'часа', 'часов'],
]

function buildFormLookup(rows: [string, string, string][]): Record<string, [string, string, string]> {
  const lookup: Record<string, [string, string, string]> = {}
  for (const row of rows) {
    for (const spelling of row) {
      if (!(spelling in lookup)) lookup[spelling] = row
    }
  }
  return lookup
}

const RU_ADJECTIVES = buildFormLookup(RU_ADJECTIVE_FORMS)
const RU_NOUNS = buildFormLookup(RU_NOUN_FORMS)

function matchCase(source: string, replacement: string): string {
  const first = source.charAt(0)
  if (source.length > 1 && source === source.toUpperCase() && source !== source.toLowerCase()) {
    return replacement.toUpperCase()
  }
  if (first === first.toUpperCase() && first !== first.toLowerCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1)
  }
  return replacement
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Substitutes `{token}` with `value` and agrees the (at most two) Cyrillic words
 * that immediately follow it: "Найдено {n} лотов" + 1 -> "Найдено 1 лот".
 * Anything the word table does not know is left byte-for-byte untouched.
 */
function substituteCount(source: string, token: string, value: string, form: PluralForm): string {
  const index = PLURAL_ORDER.indexOf(form)
  const pattern = new RegExp(`${escapeRegExp(token)}((?:\\s+[А-Яа-яЁё]+){0,2})`, 'g')
  return source.replace(pattern, (_match: string, tail: string) => {
    if (index < 0 || !tail) return value + tail
    let stopped = false
    const words = tail.split(/(\s+)/).map(chunk => {
      if (stopped || chunk.trim() === '') return chunk
      const spelling = chunk.toLowerCase()
      const adjective = RU_ADJECTIVES[spelling]
      if (adjective) return matchCase(chunk, adjective[index])
      // The noun ends the agreeing phrase, known or not.
      stopped = true
      const noun = RU_NOUNS[spelling]
      return noun ? matchCase(chunk, noun[index]) : chunk
    })
    return value + words.join('')
  })
}

function toNumber(raw: unknown): number {
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string' && raw.trim() !== '') return Number(raw)
  return Number.NaN
}

function countOf(params?: Record<string, any>): number | null {
  if (!params) return null
  for (const key of COUNT_PARAMS) {
    if (key in params) {
      const numeric = toNumber(params[key])
      if (Number.isFinite(numeric)) return numeric
    }
  }
  return null
}

type PluralBundle = Partial<Record<PluralForm, string>>

/** True for a `{ one: '…', few: '…', many: '…' }` style message value. */
function isPluralBundle(node: any): node is PluralBundle {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return false
  const keys = Object.keys(node)
  return (
    keys.length > 0 &&
    keys.every(
      key =>
        (key === 'one' || key === 'few' || key === 'many' || key === 'other') &&
        typeof node[key] === 'string'
    )
  )
}

function pickPluralVariant(bundle: PluralBundle, form: PluralForm): string {
  return bundle[form] ?? bundle.other ?? bundle.many ?? bundle.few ?? bundle.one ?? ''
}

function resolvePath(dictionary: any, keys: string[]): any {
  let node: any = dictionary
  for (const key of keys) {
    if (node && typeof node === 'object' && key in node) {
      node = node[key]
    } else {
      return undefined
    }
  }
  return node
}

function applyParams(
  source: string,
  params: Record<string, any>,
  locale: LocaleKey,
  inflect: boolean
): string {
  return Object.entries(params).reduce((str, [key, value]) => {
    const token = `{${key}}`
    if (inflect && COUNT_PARAMS.includes(key)) {
      const numeric = toNumber(value)
      if (Number.isFinite(numeric)) {
        return substituteCount(str, token, String(value), pluralForm(locale, numeric))
      }
    }
    return str.replaceAll(token, String(value))
  }, source)
}

const initialLocale: LocaleKey = getStoredLocale() || 'ru'
const currentLocale = ref<LocaleKey>(initialLocale)

// Dictionaries loaded so far. main.ts waits for the first one before mounting,
// so t() never renders an empty page; other languages load on demand.
const messages = shallowReactive<Partial<Record<LocaleKey, any>>>({})
const pending: Partial<Record<LocaleKey, Promise<void>>> = {}

export function loadLocaleMessages(locale: LocaleKey): Promise<void> {
  if (messages[locale]) return Promise.resolve()
  pending[locale] ??= localeLoaders[locale]()
    .then(mod => { messages[locale] = mod.default })
    .catch(err => {
      delete pending[locale]
      throw err
    })
  return pending[locale]!
}

// Loads the saved language, then Russian in the background as the fallback.
export async function initI18n(): Promise<void> {
  try {
    await loadLocaleMessages(initialLocale)
  } catch {
    // Chunk failed (network): fall back to Russian so the page still renders.
    if (initialLocale !== 'ru') {
      await loadLocaleMessages('ru').catch(() => {})
      currentLocale.value = 'ru'
    }
  }
  if (initialLocale !== 'ru') {
    const idle = (globalThis as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 2000))
    idle(() => { loadLocaleMessages('ru').catch(() => {}) })
  }
}

// Synchronize document lang immediately on load
if (typeof document !== 'undefined') {
  document.documentElement.lang = currentLocale.value
}

export function useI18n() {
  async function setLocale(locale: LocaleKey) {
    if (['ru', 'ky', 'tr'].includes(locale)) {
      // Switch only once the dictionary is here, so no blank labels flash.
      await loadLocaleMessages(locale)
      currentLocale.value = locale
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('itorgo_locale', locale)
        localStorage.setItem('locale', locale)
      }
      writeLocaleCookie(locale)
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale
      }
      // Best-effort: keep the server-stored preference in sync so bid/outbid
      // notifications generated later (possibly in a different session) use
      // the right language. Silently ignored if logged out or offline.
      if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) {
        apiClient.put('/api/user/profile', { locale }).catch(() => {})
      }
    }
  }

  function t(path: string, params?: Record<string, any>, fallbackStr?: string): string {
    const keys = path.split('.')
    const locale = currentLocale.value

    const dict = messages[locale] || messages.ru
    let value: any = dict ? resolvePath(dict, keys) : undefined
    if (value === undefined) {
      // Fallback to Russian (the platform's primary language) if missing in active locale
      value = messages.ru ? resolvePath(messages.ru, keys) : undefined
      if (value === undefined) {
        return fallbackStr !== undefined ? fallbackStr : ''
      }
    }

    // A key may ship explicit plural variants; when it does they are already the
    // correct form, so the runtime word agreement below must not touch them.
    let hasExplicitVariants = false
    if (isPluralBundle(value)) {
      value = pickPluralVariant(value, pluralForm(locale, countOf(params) ?? 0))
      hasExplicitVariants = true
    }

    if (typeof value !== 'string') {
      return fallbackStr !== undefined ? fallbackStr : ''
    }

    if (params) {
      return applyParams(value, params, locale, !hasExplicitVariants)
    }

    return value
  }

  const currentLocaleInfo = computed(() => {
    return supportedLocales.find(l => l.code === currentLocale.value) || supportedLocales[0]
  })

  const currentLang = computed<LocaleKey>(() => currentLocale.value)

  return {
    locale: currentLocale,
    currentLocale: currentLocaleInfo,
    currentLang,
    supportedLocales,
    setLocale,
    t,
  }
}
